import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../lib/Auth';

/**
 * The household's admin panel: who has an account, the invites that let
 * someone make one, and what the box is serving.
 *
 * Showing this page is a convenience, not the guard. Every /api/admin route is
 * refused on the server to anyone who isn't an admin, so a member who typed
 * /admin would see only the redirect below, and a member who forged their way
 * past it would get 403s.
 */

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exp;
  return `${value >= 10 || exp === 0 ? Math.round(value) : value.toFixed(1)} ${units[exp]}`;
};

const ago = (when) => {
  if (!when) return 'never';
  const ms = Date.now() - new Date(when).getTime();
  if (ms < 60 * 1000) return 'just now';
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
};

const until = (when) => {
  if (when === null) return 'never';
  const ms = new Date(when).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const hours = Math.round(ms / 3600000);
  return hours < 48 ? `in ${hours} h` : `in ${Math.round(hours / 24)} days`;
};

const day = (when) =>
  when ? new Date(when).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const EXPIRY_CHOICES = [1, 7, 30];

const STATUS_LABEL = { unused: 'Unused', used: 'Used', expired: 'Expired', revoked: 'Revoked' };

const INVITE_TABS = [
  { key: 'active', label: 'Active', statuses: ['unused'], empty: 'No invites waiting to be used' },
  { key: 'used', label: 'Used', statuses: ['used'], empty: 'No invites used yet' },
  { key: 'dead', label: 'Expired / revoked', statuses: ['expired', 'revoked'], empty: 'Nothing expired or revoked' }
];

const dateTime = (when) =>
  new Date(when).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const expiresText = (when) =>
  when === null ? 'never expires' : `expires ${dateTime(when)} (${until(when)})`;

/** A date in the form a datetime-local input takes: local time, to the minute. */
const localInput = (when) => {
  const date = new Date(when);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

/** What a choice from ExpiryField sends to the server. Nothing if it wasn't changed. */
const expiryBody = ({ choice, custom }) => {
  if (!choice) return {};
  if (choice !== 'custom') return { expiresInDays: choice };
  const at = new Date(custom);
  return { expiresAt: Number.isNaN(at.getTime()) ? '' : at.toISOString() };
};

const plural = (count, one) => `${count} ${one}${count === 1 ? '' : 's'}`;

/** A button that asks once more before doing something that can't be undone. */
const Confirm = ({ label, confirm, onConfirm, disabled }) => {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return undefined;
    const timer = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(timer);
  }, [armed]);
  return (
    <button
      type="button"
      className={`adm-act${armed ? ' is-armed' : ''}`}
      disabled={disabled}
      onClick={() => (armed ? (setArmed(false), onConfirm()) : setArmed(true))}
    >
      {armed ? confirm : label}
    </button>
  );
};

/** Copy to the clipboard, falling back to selecting the text where that isn't allowed. */
const CopyField = ({ value, label = 'Copy' }) => {
  const [copied, setCopied] = useState(false);
  const copy = async (event) => {
    const input = event.currentTarget.previousSibling;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      input.select();
    }
  };
  return (
    <div className="adm-copy">
      <input readOnly value={value} onFocus={(event) => event.target.select()} aria-label={label} />
      <button type="button" className="btn btn--sm" onClick={copy}>
        {copied ? 'Copied' : label}
      </button>
    </div>
  );
};

const Traffic = () => {
  const [range, setRange] = useState('24h');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(
    () =>
      axios
        .get(`/api/admin/traffic?range=${range}`)
        .then(({ data: body }) => {
          setData(body.data);
          setError('');
        })
        .catch(() => setError('Could not load traffic')),
    [range]
  );

  useEffect(() => {
    load();
    // Streams come and go; the rest moves slowly, but one request covers both
    const tick = setInterval(load, 10000);
    return () => clearInterval(tick);
  }, [load]);

  const peak = Math.max(1, ...(data?.series || []).map((slot) => slot.bytes));
  const tick = (at) =>
    range === '7d'
      ? new Date(at).toLocaleDateString(undefined, { weekday: 'short' })
      : new Date(at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  return (
    <>
      <section className="section">
        <div className="section-head">
          <h3 className="section-title">Streaming now</h3>
          <span className="section-note">updates every 10 s</span>
        </div>
        {data?.streams?.length ? (
          <div className="adm-list">
            {data.streams.map((s) => (
              <div key={`${s.user}-${s.kind}`} className="adm-row">
                <div className="adm-main">
                  <span className="adm-name">{s.user}</span>
                  <span className="adm-tag">{s.kind}</span>
                  {s.open > 0 && <span className="adm-tag adm-tag--live">live</span>}
                </div>
                <div className="adm-meta">
                  <span>since {new Date(s.since).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>
                  <span>{formatBytes(s.bytes)} sent</span>
                  <span>{s.ip}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty">Nobody is streaming</div>
        )}
      </section>

      <section className="section">
        <div className="section-head">
          <h3 className="section-title">Traffic</h3>
          <span className="section-note">
            {data ? `${data.totals.requests.toLocaleString()} requests · ${formatBytes(data.totals.bytes)} out` : ''}
          </span>
          <div className="seg adm-seg">
            {['24h', '7d'].map((value) => (
              <button
                key={value}
                type="button"
                className={range === value ? 'is-active' : undefined}
                onClick={() => setRange(value)}
              >
                {value}
              </button>
            ))}
          </div>
        </div>

        {error && <div className="alert">{error}</div>}

        {data && (
          <>
            <div className="adm-chart" role="img" aria-label={`Bandwidth over the last ${range}`}>
              {data.series.map((slot) => (
                <div
                  key={slot.at}
                  className="adm-bar"
                  title={`${tick(slot.at)} · ${formatBytes(slot.bytes)} · ${slot.requests.toLocaleString()} requests`}
                >
                  <span style={{ height: `${Math.max(slot.bytes ? 2 : 0, (slot.bytes / peak) * 100)}%` }} />
                </div>
              ))}
            </div>
            <div className="adm-axis">
              <span>{tick(data.series[0]?.at)}</span>
              <span>peak {formatBytes(peak)} per {range === '7d' ? 'day' : 'hour'}</span>
              <span>now</span>
            </div>

            {data.users.length > 0 && (
              <div className="adm-list adm-list--tight">
                {data.users.map((u) => (
                  <div key={u.user} className="adm-row">
                    <div className="adm-main">
                      <span className="adm-name">{u.user}</span>
                    </div>
                    <div className="adm-meta">
                      <span>{u.requests.toLocaleString()} requests</span>
                      <span>{formatBytes(u.bytes)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      <section className="section">
        <div className="section-head">
          <h3 className="section-title">Failed sign-ins</h3>
          <span className="section-note">by IP, last {range} · wrong passwords and bad invite codes</span>
        </div>
        {data?.failedLogins?.length ? (
          <div className="adm-list">
            {data.failedLogins.map((f) => (
              <div key={f.ip} className="adm-row">
                <div className="adm-main">
                  <span className="adm-name adm-mono">{f.ip}</span>
                  <span className="adm-tag">{f.count}×</span>
                </div>
                <div className="adm-meta">
                  {f.login ? <span>{f.login} sign-in</span> : null}
                  {f.invite ? <span>{f.invite} invite</span> : null}
                  <span>last {ago(f.lastAt)}</span>
                  {f.usernames.length > 0 && <span>tried: {f.usernames.join(', ')}</span>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty">No failed sign-ins</div>
        )}
      </section>
    </>
  );
};

/** The presets, or a date and time of the admin's own. */
const ExpiryField = ({ value, onChange, blank, startFrom }) => {
  const pick = (choice) => {
    // Open the picker on something sensible: the current expiry, or a day from now
    const custom = value.custom || localInput(startFrom || Date.now() + 24 * 3600000);
    onChange({ choice, custom });
  };
  return (
    <>
      <label className="field adm-expiry">
        <span>EXPIRES IN</span>
        <select value={value.choice} onChange={(event) => pick(event.target.value)}>
          {blank && <option value="">{blank}</option>}
          {EXPIRY_CHOICES.map((days) => (
            <option key={days} value={days}>
              {days} day{days === 1 ? '' : 's'}
            </option>
          ))}
          <option value="never">Never</option>
          <option value="custom">Pick a date…</option>
        </select>
      </label>
      {value.choice === 'custom' && (
        <label className="field adm-expiry-at">
          <span>EXPIRES ON</span>
          <input
            type="datetime-local"
            value={value.custom}
            min={localInput(Date.now())}
            onChange={(event) => onChange({ ...value, custom: event.target.value })}
          />
        </label>
      )}
    </>
  );
};

/** Change an invite's note, and its expiry while it can still be spent or brought back. */
const InviteEditor = ({ invite, busy, act, say, onClose }) => {
  const [note, setNote] = useState(invite.note);
  const [expiry, setExpiry] = useState({ choice: '', custom: '' });
  const canExpire = invite.status === 'unused' || invite.status === 'expired';
  const save = (event) => {
    event.preventDefault();
    const body = { note, ...expiryBody(expiry) };
    act(`edit-${invite.id}`, () => axios.patch(`/api/admin/invites/${invite.id}`, body), () => {
      say('Invite changed');
      onClose();
    });
  };
  return (
    <form className="adm-invite adm-panel" onSubmit={save}>
      <label className="field">
        <span>NOTE</span>
        <input value={note} onChange={(event) => setNote(event.target.value)} maxLength={60} />
      </label>
      {canExpire && (
        <ExpiryField
          value={expiry}
          onChange={setExpiry}
          blank={invite.status === 'expired' ? 'Stay expired' : 'No change'}
          startFrom={invite.status === 'unused' ? invite.expiresAt : null}
        />
      )}
      <button type="submit" className="adm-act" disabled={Boolean(busy)}>
        Save
      </button>
      <button type="button" className="adm-act" onClick={onClose}>
        Cancel
      </button>
    </form>
  );
};

const Invites = ({ invites, busy, act, say }) => {
  const [note, setNote] = useState('');
  const [expiry, setExpiry] = useState({ choice: '7', custom: '' });
  const [fresh, setFresh] = useState(null); // the invite just made, with its code
  const [tab, setTab] = useState('active');
  const [selected, setSelected] = useState(() => new Set());
  const [editing, setEditing] = useState(null);

  const makeInvite = (event) => {
    event.preventDefault();
    act(
      'invite',
      () => axios.post('/api/admin/invites', { note, ...expiryBody(expiry) }),
      ({ code, invite }) => {
        setFresh({ ...invite, code, link: `${window.location.origin}/signup?code=${code}` });
        setNote('');
      }
    );
  };

  const current = INVITE_TABS.find((t) => t.key === tab);
  const inTab = (t) => invites.filter((invite) => t.statuses.includes(invite.status));
  const rows = inTab(current);
  // Worked out from what is on screen, so an invite that moved tab or vanished
  // since it was ticked can't be acted on unseen
  const picked = rows.filter((invite) => selected.has(invite.id)).map((invite) => invite.id);
  const dead = invites.filter((invite) => invite.status === 'expired' || invite.status === 'revoked').length;

  const switchTab = (key) => {
    setTab(key);
    setSelected(new Set());
    setEditing(null);
  };

  const toggle = (id) =>
    setSelected((was) => {
      const next = new Set(was);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const bulk = (path, verb) =>
    act(`bulk-${path}`, () => axios.post(`/api/admin/invites/${path}`, { ids: picked }), ({ changed, skipped }) => {
      say(`${plural(changed, 'invite')} ${verb}${skipped ? `, ${skipped} skipped` : ''}`);
      setSelected(new Set());
    });

  return (
    <section className="section">
      <div className="section-head">
        <h3 className="section-title">Invites</h3>
        <span className="section-note">single use · the code is shown once</span>
      </div>

      <form className="adm-invite" onSubmit={makeInvite}>
        <label className="field">
          <span>NOTE (OPTIONAL)</span>
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="sister"
            maxLength={60}
          />
        </label>
        <ExpiryField value={expiry} onChange={setExpiry} />
        <button type="submit" className="btn btn--play adm-make" disabled={busy === 'invite'}>
          {busy === 'invite' ? 'Making…' : 'New invite'}
        </button>
      </form>

      {fresh && (
        <div className="adm-fresh">
          <div className="adm-fresh-head">
            <b>Invite{fresh.note ? ` for ${fresh.note}` : ''}</b>
            <span>{expiresText(fresh.expiresAt)} · copy it now, it won't be shown again</span>
          </div>
          <CopyField value={fresh.link} label="Copy link" />
          <CopyField value={fresh.code} label="Copy code" />
          <button type="button" className="adm-act" onClick={() => setFresh(null)}>
            Done
          </button>
        </div>
      )}

      <div className="adm-tools">
        <div className="seg">
          {INVITE_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={tab === t.key ? 'is-active' : undefined}
              onClick={() => switchTab(t.key)}
            >
              {t.label} {inTab(t).length}
            </button>
          ))}
        </div>
        {dead > 0 && (
          <Confirm
            label={`Clear expired & revoked (${dead})`}
            confirm={`Delete all ${dead}?`}
            disabled={Boolean(busy)}
            onConfirm={() =>
              act(
                'clear-dead',
                () => axios.post('/api/admin/invites/clear-dead'),
                ({ changed }) => say(`${plural(changed, 'invite')} deleted`)
              )
            }
          />
        )}
      </div>

      {rows.length > 0 && (
        <div className="adm-tools adm-tools--select">
          <label className="adm-check">
            <input
              type="checkbox"
              checked={picked.length === rows.length}
              onChange={() =>
                setSelected(picked.length === rows.length ? new Set() : new Set(rows.map((invite) => invite.id)))
              }
            />
            <span>{picked.length ? `${picked.length} selected` : 'Select all'}</span>
          </label>
          {picked.length > 0 &&
            (tab === 'active' ? (
              <Confirm
                label="Revoke selected"
                confirm={`Revoke ${picked.length}?`}
                disabled={Boolean(busy)}
                onConfirm={() => bulk('bulk-revoke', 'revoked')}
              />
            ) : (
              <Confirm
                label="Delete selected"
                confirm={`Delete ${picked.length}?`}
                disabled={Boolean(busy)}
                onConfirm={() => bulk('bulk-delete', 'deleted')}
              />
            ))}
        </div>
      )}

      {rows.length ? (
        <div className="adm-list">
          {rows.map((invite) => (
            <div key={invite.id} className={`adm-row${invite.status !== 'unused' ? ' is-dim' : ''}`}>
              <div className="adm-main">
                <input
                  type="checkbox"
                  className="adm-tick"
                  aria-label={`Select invite ${invite.hint}`}
                  checked={selected.has(invite.id)}
                  onChange={() => toggle(invite.id)}
                />
                <span className="adm-name adm-mono">{invite.hint}-····</span>
                {invite.note && <span className="adm-note">{invite.note}</span>}
                <span className={`adm-tag adm-tag--${invite.status}`}>
                  {STATUS_LABEL[invite.status]}
                  {invite.status === 'used' && invite.usedBy ? ` by ${invite.usedBy}` : ''}
                </span>
              </div>
              <div className="adm-meta">
                <span>made {day(invite.createdAt)}</span>
                {invite.status === 'unused' && <span>{expiresText(invite.expiresAt)}</span>}
                {invite.status === 'expired' && <span>expired {dateTime(invite.expiresAt)}</span>}
                {invite.status === 'revoked' && <span>revoked {day(invite.revokedAt)}</span>}
                {invite.status === 'used' && <span>used {ago(invite.usedAt)}</span>}
              </div>
              <div className="adm-acts">
                <button
                  type="button"
                  className="adm-act"
                  onClick={() => setEditing(editing === invite.id ? null : invite.id)}
                >
                  Edit
                </button>
                {invite.status === 'unused' ? (
                  <Confirm
                    label="Revoke"
                    confirm="Revoke it?"
                    disabled={busy === `revoke-${invite.id}`}
                    onConfirm={() =>
                      act(
                        `revoke-${invite.id}`,
                        () => axios.post(`/api/admin/invites/${invite.id}/revoke`),
                        () => say('Invite revoked')
                      )
                    }
                  />
                ) : (
                  <Confirm
                    label="Delete"
                    confirm="Delete it?"
                    disabled={busy === `delete-${invite.id}`}
                    onConfirm={() =>
                      act(
                        `delete-${invite.id}`,
                        () => axios.delete(`/api/admin/invites/${invite.id}`),
                        () => say('Invite deleted')
                      )
                    }
                  />
                )}
              </div>
              {editing === invite.id && (
                <InviteEditor invite={invite} busy={busy} act={act} say={say} onClose={() => setEditing(null)} />
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="empty">{current.empty}</div>
      )}
    </section>
  );
};

/** One account's signed-in devices, each of which can be signed out alone. */
const SessionList = ({ username, count, busy, act, say }) => {
  const [rows, setRows] = useState(null);
  const path = `/api/admin/users/${encodeURIComponent(username)}/sessions`;
  const load = useCallback(
    () =>
      axios
        .get(path)
        .then(({ data: body }) => setRows(body.data))
        .catch(() => setRows([])),
    [path]
  );
  // The 30-second refresh changes the count when a session comes or goes
  useEffect(() => {
    load();
  }, [load, count]);

  if (!rows) return <div className="adm-panel adm-meta">Loading…</div>;
  if (!rows.length) return <div className="adm-panel adm-meta">Not signed in anywhere</div>;
  return (
    <div className="adm-panel adm-sessions">
      {rows.map((row) => (
        <div key={row.id} className="adm-session">
          <div className="adm-meta">
            <span>signed in {day(row.createdAt)}</span>
            <span>lasts until {day(row.expiresAt)}</span>
            {row.current && <span className="adm-tag">this device</span>}
          </div>
          <Confirm
            label="Sign out"
            confirm="Sign it out?"
            disabled={Boolean(busy)}
            onConfirm={() =>
              act(`session-${row.id}`, () => axios.delete(`/api/admin/sessions/${row.id}`), () => {
                if (row.current) window.location.assign('/login');
                else say('Signed out');
                load();
              })
            }
          />
        </div>
      ))}
    </div>
  );
};

const Admin = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');
  const [busy, setBusy] = useState('');

  const [reset, setReset] = useState(null); // { username, password } just issued
  const [openSessions, setOpenSessions] = useState(null); // whose sessions are showing

  const load = useCallback(
    () =>
      axios
        .get('/api/admin/overview')
        .then(({ data: body }) => {
          setData(body.data);
          setError('');
        })
        .catch((err) => setError(err.response?.data?.error || 'Could not load accounts')),
    []
  );

  useEffect(() => {
    load();
    const tick = setInterval(load, 30000);
    return () => clearInterval(tick);
  }, [load]);

  const say = (message) => {
    setFlash(message);
    setTimeout(() => setFlash(''), 2600);
  };

  const act = async (key, request, done) => {
    setBusy(key);
    setError('');
    try {
      const { data: body } = await request();
      if (done) done(body.data);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'That did not work');
    } finally {
      setBusy('');
    }
  };

  if (user && !user.admin) return <Navigate to="/home" replace />;
  if (!data && !error) return <div className="page loading">LOADING ACCOUNTS</div>;

  const seats = data?.seats;

  return (
    <div className="page adm">
      <div className="page-head">
        <div>
          <h1 className="page-title">Admin</h1>
          <p className="page-sub">accounts, invites and what the box is serving</p>
        </div>
        {flash && <span className="section-note">{flash}</span>}
      </div>

      {error && <div className="alert">{error}</div>}

      {data && (
        <div className="stats adm-stats">
          <div className="stat">
            <div className="stat-k">ACCOUNTS</div>
            <div className="stat-v">
              <span className="stat-big">{seats.accounts}</span>
              <span className="stat-unit">on the box</span>
            </div>
          </div>
          <div className="stat">
            <div className="stat-k">INVITES OUT</div>
            <div className="stat-v">
              <span className="stat-big">{seats.pending}</span>
              <span className="stat-unit">waiting to be used</span>
            </div>
          </div>
          <div className="stat">
            <div className="stat-k">STREAMING</div>
            <div className="stat-v">
              <span className="stat-big">{data.users.filter((u) => u.streaming).length}</span>
              <span className="stat-unit">accounts now</span>
            </div>
          </div>
        </div>
      )}

      {data && <Invites invites={data.invites} busy={busy} act={act} say={say} />}

      {data && (
        <section className="section">
          <div className="section-head">
            <h3 className="section-title">Accounts</h3>
            <span className="section-note">{data.users.length}</span>
            <button
              type="button"
              className="adm-act adm-seg"
              disabled={Boolean(busy)}
              title="Removes sessions that are expired or belong to a deleted or disabled account. Signs nobody out."
              onClick={() =>
                act('clear-stale', () => axios.post('/api/admin/sessions/clear-stale'), ({ removed }) =>
                  say(removed ? `${plural(removed, 'stale session')} removed` : 'No stale sessions')
                )
              }
            >
              Clear stale sessions
            </button>
          </div>

          {reset && (
            <div className="adm-fresh">
              <div className="adm-fresh-head">
                <b>New password for {reset.username}</b>
                <span>give it to them; they were signed out everywhere</span>
              </div>
              <CopyField value={reset.password} label="Copy" />
              <button type="button" className="adm-act" onClick={() => setReset(null)}>
                Done
              </button>
            </div>
          )}

          <div className="adm-list">
            {data.users.map((u) => {
              const me = u.username === data.me;
              const key = (what) => `${what}-${u.username}`;
              const path = `/api/admin/users/${encodeURIComponent(u.username)}`;
              return (
                <div key={u.username} className={`adm-row${u.disabled ? ' is-dim' : ''}`}>
                  <div className="adm-main">
                    <span className="adm-name">{u.username}</span>
                    {u.role === 'admin' && <span className="adm-tag">admin</span>}
                    {me && <span className="adm-tag">you</span>}
                    {u.disabled && <span className="adm-tag adm-tag--revoked">disabled</span>}
                    {u.streaming && <span className="adm-tag adm-tag--live">streaming</span>}
                  </div>
                  <div className="adm-meta">
                    <span>joined {day(u.createdAt)}</span>
                    <span>seen {ago(u.lastSeenAt)}</span>
                    <span>
                      {u.sessions} session{u.sessions === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="adm-acts">
                    <button
                      type="button"
                      className="adm-act"
                      disabled={!u.sessions}
                      onClick={() => setOpenSessions(openSessions === u.username ? null : u.username)}
                    >
                      Sessions
                    </button>
                    <Confirm
                      label="Reset password"
                      confirm="Reset it?"
                      disabled={Boolean(busy)}
                      onConfirm={() =>
                        act(key('reset'), () => axios.post(`${path}/reset-password`), (result) => setReset(result))
                      }
                    />
                    <button
                      type="button"
                      className="adm-act"
                      disabled={Boolean(busy) || !u.sessions}
                      onClick={() =>
                        act(key('revoke'), () => axios.post(`${path}/revoke-sessions`), () =>
                          // Revoking your own includes this one
                          me ? window.location.assign('/login') : say(`${u.username} signed out everywhere`)
                        )
                      }
                    >
                      Revoke sessions
                    </button>
                    {!me && (
                      <button
                        type="button"
                        className="adm-act"
                        disabled={Boolean(busy)}
                        onClick={() =>
                          act(key('disable'), () => axios.post(`${path}/disable`, { disabled: !u.disabled }), () =>
                            say(u.disabled ? `${u.username} enabled` : `${u.username} disabled`)
                          )
                        }
                      >
                        {u.disabled ? 'Enable' : 'Disable'}
                      </button>
                    )}
                    {!me && (
                      <Confirm
                        label="Delete"
                        confirm={`Delete ${u.username} and their lists?`}
                        disabled={Boolean(busy)}
                        onConfirm={() =>
                          act(key('delete'), () => axios.delete(path), () => say(`${u.username} deleted`))
                        }
                      />
                    )}
                  </div>
                  {openSessions === u.username && (
                    <SessionList username={u.username} count={u.sessions} busy={busy} act={act} say={say} />
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {data && <Traffic />}
    </div>
  );
};

export default Admin;
